import { Spinner } from '@chakra-ui/react'
import { Card } from '@nextui-org/react'
import { useQuery } from '@tanstack/react-query'
import { FC } from 'react'
import {
  getTankWaterQualityChart,
  type BaseChartData,
} from '@/api/chart/chart.service'
import useChartStore from '../../store/chart/useChartStore'
import '../../styles/responsive.css'
import { EmptyChartState } from './EmptyChartState'
import { SingleDomainPlot } from './plot/SingleDomainPlot'

export type WaterQualityChartComponentProps = {
  schoolColors: { [id: string]: string }
}

/**
 * NH3 & NO2 수질 차트 컴포넌트
 * 두 개의 차트 (NH3, NO2)를 렌더링
 */
export const WaterQualityChartComponent: FC<WaterQualityChartComponentProps> = ({
  schoolColors,
}) => {
  const { selectedSchools, selectedTanks } = useChartStore()
  const labels = Object.fromEntries(selectedTanks.map((t) => [t.schoolId, t.tankName]))

  const waterQualityQuery = useQuery({
    queryKey: ['chart', 'waterQuality', selectedSchools],
    queryFn: async () => {
      if (selectedSchools.length === 0) return null
      return await getTankWaterQualityChart(selectedSchools)
    },
    staleTime: 3600,
    refetchOnMount: false,
  })

  const renderChart = (
    title: string,
    data: BaseChartData | undefined,
    isLoading: boolean,
    isError: boolean
  ) => (
    <div className="flex flex-col">
      <div className="mx-1 mb-1 flex flex-row justify-between">
        <p className="font-bold text-[1.25rem]">{title}</p>
      </div>
      {isLoading ? (
        <Card
          shadow="none"
          className="h-[300px] sm:h-[400px] md:h-[450px] lg:h-[500px] min-w-[100%] border-[1px] border-tdpldarkgrey border-opacity-60
          flex items-center justify-center"
        >
          <Spinner color="#B98CA4" emptyColor="gray.200" size="lg" />
        </Card>
      ) : isError ? (
        <EmptyChartState title={title} message="Failed to load chart data. Please try again." />
      ) : !data ? (
        <EmptyChartState title={title} message="No data available for the selected criteria" />
      ) : (
        <Card
          shadow="none"
          className="min-w-[100%] border-[1px] border-tdpldarkgrey border-opacity-60"
        >
          <div className="p-0 h-[300px] sm:h-[400px] md:h-[450px] lg:h-[500px] overflow-hidden">
            {Object.keys(data.xyPointsPerSchoolId).length > 0 ? (
              <SingleDomainPlot
                schoolColors={schoolColors}
                property="nh3no2"
                chartResponse={data}
                timeUnit="day"
                labels={labels}
              />
            ) : (
              <EmptyChartState title={title} message="No data available for this property" />
            )}
          </div>
        </Card>
      )}
    </div>
  )

  return (
    <>
      {renderChart(
        'NH3',
        waterQualityQuery.data?.nh3,
        waterQualityQuery.isLoading,
        waterQualityQuery.isError
      )}
      {renderChart(
        'NO2',
        waterQualityQuery.data?.no2,
        waterQualityQuery.isLoading,
        waterQualityQuery.isError
      )}
    </>
  )
}
